/**
 * AES-GCM encryption/decryption for the master mnemonic.
 *
 * Uses Web Crypto API (crypto.subtle) for AES-256-GCM encryption,
 * with key derivation from PIN via PBKDF2 (from crypto-kdf.ts).
 *
 * The encrypted payload is stored in SecureStorage (localStorage on web,
 * Keychain/Keystore on native). A fresh salt and IV are generated for
 * every encryption operation — never reused.
 *
 * SECURITY:
 * - AES-GCM with 256-bit keys (derived via PBKDF2 600k iterations)
 * - 16-byte random salt per encryption
 * - 12-byte random IV per encryption (NIST recommended for GCM)
 * - Wrong PIN detection via GCM authentication tag failure
 * - Never logs mnemonic, PIN, or key material
 *
 * @module crypto-encrypt
 */

import { bytesToHex, hexToBytes } from '@noble/hashes/utils';
import { deriveKeyFromPin } from './crypto-kdf';

/** Current payload format version. */
const PAYLOAD_VERSION = 1;

/** Salt length in bytes (128 bits). */
const SALT_LENGTH = 16;

/** IV length in bytes (96 bits, NIST recommended for AES-GCM). */
const IV_LENGTH = 12;

/**
 * Serialized encrypted payload stored in SecureStorage.
 *
 * All binary fields are hex-encoded for safe JSON serialization.
 */
interface EncryptedPayload {
  /** Hex-encoded salt used for PBKDF2 key derivation. */
  readonly salt: string;
  /** Hex-encoded initialization vector for AES-GCM. */
  readonly iv: string;
  /** Hex-encoded AES-GCM ciphertext (includes authentication tag). */
  readonly data: string;
  /** Payload format version for future migration support. */
  readonly v: number;
}

/**
 * Error thrown when PIN verification fails during decryption.
 *
 * This replaces the raw DOMException that crypto.subtle.decrypt throws
 * on authentication failure, providing a user-friendly message.
 */
export class PinError extends Error {
  constructor(message = 'Incorrect PIN') {
    super(message);
    this.name = 'PinError';
  }
}

/**
 * Import a raw PBKDF2-derived key into Web Crypto for AES-GCM operations.
 *
 * @param rawKey - 32-byte key from deriveKeyFromPin
 * @returns CryptoKey suitable for AES-GCM encrypt/decrypt
 */
async function importKey(rawKey: Uint8Array): Promise<CryptoKey> {
  // Web Crypto expects BufferSource (ArrayBuffer), but @noble/hashes Uint8Array
  // has buffer typed as ArrayBufferLike. Copy into a fresh ArrayBuffer to satisfy
  // the type constraint without unsafe casts.
  const keyBuffer = new ArrayBuffer(rawKey.byteLength);
  new Uint8Array(keyBuffer).set(rawKey);
  return crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Encrypt a mnemonic with the user's PIN using AES-256-GCM.
 *
 * Generates a fresh random salt and IV for each call. The returned
 * string is a JSON-encoded EncryptedPayload suitable for storage.
 *
 * @param mnemonic - BIP-39 mnemonic to encrypt
 * @param pin - User's PIN (typically 6 digits)
 * @param iterations - PBKDF2 iterations (default: production value, reduce in tests)
 * @returns JSON string of EncryptedPayload
 */
export async function encryptMnemonic(
  mnemonic: string,
  pin: string,
  iterations?: number,
): Promise<string> {
  if (!mnemonic || mnemonic.trim() === '') {
    throw new Error('Mnemonic must not be empty');
  }

  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const rawKey = deriveKeyFromPin(pin, salt, iterations);
  const cryptoKey = await importKey(rawKey);

  const encoder = new TextEncoder();
  const plaintext = encoder.encode(mnemonic);

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, plaintext);

  const payload: EncryptedPayload = {
    salt: bytesToHex(salt),
    iv: bytesToHex(iv),
    data: bytesToHex(new Uint8Array(ciphertext)),
    v: PAYLOAD_VERSION,
  };

  return JSON.stringify(payload);
}

/**
 * Decrypt a mnemonic with the user's PIN using AES-256-GCM.
 *
 * Parses the EncryptedPayload, derives the key from PIN + salt,
 * and decrypts. If the PIN is wrong, GCM authentication fails
 * and a PinError is thrown (not a raw DOMException).
 *
 * @param encrypted - JSON string of EncryptedPayload (from encryptMnemonic)
 * @param pin - User's PIN
 * @param iterations - PBKDF2 iterations (default: production value, reduce in tests)
 * @returns Decrypted mnemonic string
 * @throws PinError if the PIN is incorrect
 * @throws Error if the payload format is invalid
 */
export async function decryptMnemonic(
  encrypted: string,
  pin: string,
  iterations?: number,
): Promise<string> {
  let payload: EncryptedPayload;
  try {
    payload = JSON.parse(encrypted) as EncryptedPayload;
  } catch {
    throw new Error('Invalid encrypted payload format');
  }

  if (payload.v !== PAYLOAD_VERSION) {
    throw new Error(`Unsupported payload version: ${String(payload.v)}`);
  }

  const salt = hexToBytes(payload.salt);
  const ciphertext = hexToBytes(payload.data);

  // Copy iv and ciphertext into fresh ArrayBuffers for Web Crypto compatibility.
  // hexToBytes returns Uint8Array<ArrayBufferLike> but Web Crypto expects BufferSource
  // (backed by ArrayBuffer, not SharedArrayBuffer).
  const ivBuffer = new ArrayBuffer(hexToBytes(payload.iv).byteLength);
  new Uint8Array(ivBuffer).set(hexToBytes(payload.iv));

  const ciphertextBuffer = new ArrayBuffer(ciphertext.byteLength);
  new Uint8Array(ciphertextBuffer).set(ciphertext);

  const rawKey = deriveKeyFromPin(pin, salt, iterations);
  const cryptoKey = await importKey(rawKey);

  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBuffer },
      cryptoKey,
      ciphertextBuffer,
    );

    const decoder = new TextDecoder();
    return decoder.decode(plaintext);
  } catch {
    throw new PinError();
  }
}
